import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {Column, Row} from 'react-table';
import {toast} from 'react-toastify';

import {validateCode} from 'api';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {ReactComponent as TrashIcon} from 'assets/svg/trash.svg';
import {InvoiceLineTypeBanner} from 'components/pages/DocumentsCreate/InvoiceNew/Pages/InvoiceLines/components/InvoiceLineTypeBanner';
import {LineControlComponent} from 'components/pages/DocumentsCreate/InvoiceNew/Pages/InvoiceLines/components/LineControl';
import {
    AddInvoiceLineForm,
    ButtonGroup,
    Container,
    Headline,
    HeaderGroupItem,
    Highlighted,
    RequiredTag,
    SectionDescription,
} from 'components/pages/DocumentsCreate/InvoiceNew/Pages/InvoiceLines/styled';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {Table} from 'components/ui-kit/Table2.0';
import {DocumentReportType} from 'core/dictionaries';
import {RestError, showRestError} from 'core/errors';
import {colors} from 'theme';
import {TransferDocumentLine} from 'types';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes} from 'types/gtins';
import {CodeResponseType} from 'types/validation';
import {invoiceValidationWithTransportCodes} from 'utils/markingCodes';
import {hasOnlyLatinLetters} from 'utils/string';

import {
    CodeValue,
    ExistingLinesTable,
    LineActions,
    LinesTable,
    ProductCode,
    TableHeader,
    TransportCodesModalContent,
} from './styled';
import {
    areTransferDocumentLinesValid,
    collectTransferDocumentCodes,
    collectTransferDocumentLinesWithData,
    copyTransferDocumentLineData,
    countTransferDocumentCodes,
    createTransferDocumentLine,
    isTransportLine,
    updateTransferDocumentLine,
} from './transportCodes';

type LineRow = TransferDocumentLine & {isDraft?: boolean};

type TableLineRow = LineRow & {
    editable: boolean;
    showErrors: boolean;
    draftCode?: string;
    draftAmount?: string;
    draftVat?: string;
    codeIsValid?: boolean;
    canCopy?: boolean;
    canRemove?: boolean;
    depth?: number;
    onCodeChange?: (value: string) => void;
    onCodeBlur?: () => void;
    onCodePaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
    onAmountChange?: (value: string) => void;
    onVatChange?: (value: string) => void;
    onUpdate?: (field: 'lineAmount' | 'vat', value: string) => void;
    onCopy?: () => void;
    onRemove?: () => void;
    markingCodePlaceholder?: string;
    amountPlaceholder?: string;
    vatPlaceholder?: string;
};

interface Props {
    currency: string;
    editable: boolean;
    lines: TransferDocumentLine[];
    showErrors: boolean;
    onChange: (lines: TransferDocumentLine[]) => void;
}

const roundMoney = (value: number) => Math.round(value * 100) / 100;

const LineNumberCell = ({row, value}: {row: Row<TableLineRow>; value: number}) =>
    row.original.isDraft ? null : value || row.index + 1;

const MarkingCodeCell = ({row}: {row: Row<TableLineRow>}) => {
    const line = row.original;
    return line.isDraft ? (
        <Input
            size="sm"
            value={line.draftCode}
            disabled={!line.editable}
            hasError={line.showErrors && !line.codeIsValid}
            placeholder={line.markingCodePlaceholder}
            onChange={(event) => line.onCodeChange?.(event.target.value)}
            onBlur={line.onCodeBlur}
            onPaste={line.onCodePaste}
        />
    ) : (
        <CodeValue depth={line.depth ?? row.depth}>
            <span>{line.markingCode}</span>
            {!isTransportLine(line) && <ProductCode>{line.productCode}</ProductCode>}
        </CodeValue>
    );
};

const LineNumberInput = ({
    value,
    step,
    hasError,
    onCommit,
}: {
    value: number | '';
    step: string;
    hasError?: boolean;
    onCommit: (value: string) => void;
}) => {
    const [inputValue, setInputValue] = useState(String(value));
    useEffect(() => setInputValue(String(value)), [value]);

    return (
        <Input
            size="sm"
            type="number"
            min="0"
            step={step}
            value={inputValue}
            hasError={hasError}
            placeholder="Enter value"
            onChange={(event) => setInputValue(event.target.value)}
            onBlur={() => onCommit(inputValue)}
            onKeyDown={(event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    onCommit(inputValue);
                }
            }}
        />
    );
};

const LineAmountCell = ({row}: {row: Row<TableLineRow>}) => {
    const line = row.original;
    if (isTransportLine(line)) {
        return line.isDraft ? <Input size="sm" disabled /> : null;
    }
    if (line.isDraft) {
        return (
            <Input
                size="sm"
                type="number"
                min="0"
                step="0.01"
                value={line.draftAmount}
                disabled={!line.editable || !line.codeIsValid}
                hasError={line.showErrors && (!line.draftAmount || Number(line.draftAmount) <= 0)}
                placeholder={line.amountPlaceholder}
                onChange={(event) => line.onAmountChange?.(event.target.value)}
            />
        );
    }
    return line.editable ? (
        <LineNumberInput
            step="0.01"
            value={line.lineAmount}
            hasError={line.showErrors && Number(line.lineAmount) <= 0}
            onCommit={(value) => line.onUpdate?.('lineAmount', value)}
        />
    ) : (
        <>{line.lineAmount}</>
    );
};

const VatCell = ({row}: {row: Row<TableLineRow>}) => {
    const line = row.original;
    if (isTransportLine(line)) {
        return line.isDraft ? <Input size="sm" disabled /> : null;
    }
    if (line.isDraft) {
        return (
            <Input
                size="sm"
                type="number"
                min="0"
                step="0.1"
                value={line.draftVat}
                disabled={!line.editable || !line.codeIsValid}
                hasError={line.showErrors && line.draftVat === ''}
                placeholder={line.vatPlaceholder}
                onChange={(event) => line.onVatChange?.(event.target.value)}
            />
        );
    }
    return line.editable ? (
        <LineNumberInput
            step="0.1"
            value={line.vat}
            hasError={line.showErrors && (line.vat === '' || Number.isNaN(Number(line.vat)))}
            onCommit={(value) => line.onUpdate?.('vat', value)}
        />
    ) : (
        <>{line.vat}</>
    );
};

const TaxAmountCell = ({row}: {row: Row<TableLineRow>}) => {
    const line = row.original;
    if (isTransportLine(line)) {
        return line.isDraft ? <Input size="sm" disabled /> : null;
    }
    return line.isDraft ? (
        <Input
            size="sm"
            disabled
            placeholder="Auto"
            value={roundMoney((Number(line.draftAmount) * Number(line.draftVat)) / 100) || ''}
        />
    ) : line.lineTaxAmount === '' ? (
        <Input size="sm" disabled placeholder="Auto" value="" />
    ) : (
        <>{line.lineTaxAmount}</>
    );
};

const AmountWithVatCell = ({row}: {row: Row<TableLineRow>}) => {
    const line = row.original;
    if (isTransportLine(line)) {
        return line.isDraft ? <Input size="sm" disabled /> : null;
    }
    const value = line.isDraft
        ? roundMoney(
              Number(line.draftAmount) + (Number(line.draftAmount) * Number(line.draftVat)) / 100,
          ) || ''
        : line.lineAmount === '' || line.vat === ''
        ? ''
        : roundMoney(Number(line.lineAmount) + Number(line.lineTaxAmount));
    return line.isDraft || value === '' ? (
        <Input size="sm" disabled placeholder="Auto" value={value} />
    ) : (
        <>{value}</>
    );
};

const ActionsCell = ({row}: {row: Row<TableLineRow>}) => {
    const line = row.original;
    if (line.isDraft || !line.editable || (!line.canCopy && !line.canRemove)) return null;
    return (
        <LineActions onClick={(event) => event.stopPropagation()}>
            {line.canCopy && (
                <LineControlComponent
                    fieldsLength={2}
                    allowToCopyData
                    allowToDuplicateDown={false}
                    allowToDelete={Boolean(line.canRemove)}
                    onCopyData={() => line.onCopy?.()}
                    onDuplicateDown={() => undefined}
                    onRemoveField={() => line.onRemove?.()}
                />
            )}
            {!line.canCopy && line.canRemove && (
                <IconButton
                    type="button"
                    onClick={() => line.onRemove?.()}
                    icon={
                        <Icon source={TrashIcon} width={16} height={16} fillColor={colors.danger} />
                    }
                />
            )}
        </LineActions>
    );
};

export const TransferDocumentLinesStep = ({
    currency,
    editable,
    lines,
    showErrors,
    onChange,
}: Props) => {
    const {t} = useTranslation();
    const [code, setCode] = useState('');
    const [amount, setAmount] = useState('');
    const [vat, setVat] = useState('');
    const [codeInfo, setCodeInfo] = useState<CodeResponseType['info'] | null>(null);
    const [scanShow, setScanShow] = useState(false);
    const [pending, setPending] = useState(false);
    const validationRevision = useRef(0);
    useEffect(
        () => () => {
            validationRevision.current += 1;
        },
        [],
    );
    const changeCode = useCallback((value: string) => {
        validationRevision.current += 1;
        setCode(value.trim().replace(/\s/g, ''));
        setCodeInfo(null);
        setPending(false);
    }, []);
    const [selectedTransportCode, setSelectedTransportCode] = useState<string | null>(null);
    const codeIsTransport = Boolean(
        codeInfo?.packagingType && !CUSTOMER_PACKAGE_TYPES.includes(codeInfo.packagingType),
    );

    const validateMarkingCode = useCallback(
        async (markingCode: string) => {
            const normalizedCode = markingCode.trim().replace(/\s/g, '');
            const revision = ++validationRevision.current;
            setCodeInfo(null);
            if (!normalizedCode) return null;
            if (!hasOnlyLatinLetters(normalizedCode)) {
                toast.warning(t('components.scan.layoutWarn'));
                return null;
            }
            setPending(true);
            try {
                const {data} = await validateCode({
                    type: DocumentReportType.TRANSFER_DOCUMENT,
                    markingCode: normalizedCode,
                });
                if (revision !== validationRevision.current) return null;
                const errors = invoiceValidationWithTransportCodes(
                    data,
                    collectTransferDocumentCodes(lines),
                );
                if (errors.length) {
                    errors.forEach((message) => toast.error(message));
                    return null;
                }
                return data.info;
            } catch (error) {
                if (revision === validationRevision.current) showRestError(error as RestError);
                return null;
            } finally {
                if (revision === validationRevision.current) setPending(false);
            }
        },
        [lines, t],
    );

    const checkCode = useCallback(async () => {
        const info = await validateMarkingCode(code);
        if (info) setCodeInfo(info);
    }, [code, validateMarkingCode]);

    const appendValidatedLine = useCallback(
        (
            info: CodeResponseType['info'],
            lineAmount: number | '' = '',
            lineVat: number | '' = '',
        ) => {
            const newLine = {
                ...createTransferDocumentLine(info, lineAmount, lineVat),
                lineNumber: lines.length + 1,
            };

            onChange([...lines, newLine]);
            changeCode('');
            setAmount('');
            setVat('');
            setCodeInfo(null);
        },
        [changeCode, lines, onChange],
    );

    const addLine = useCallback(async () => {
        const validatedCodeInfo = codeInfo ?? (await validateMarkingCode(code));
        if (!validatedCodeInfo) return;

        const isTransport = !CUSTOMER_PACKAGE_TYPES.includes(validatedCodeInfo.packagingType);
        const lineAmount = Number(amount);
        const lineVat = Number(vat);
        if (!isTransport && (!lineAmount || vat === '' || Number.isNaN(lineVat))) {
            setCodeInfo(validatedCodeInfo);
            return;
        }

        appendValidatedLine(validatedCodeInfo, lineAmount, lineVat);
    }, [amount, appendValidatedLine, code, codeInfo, validateMarkingCode, vat]);

    const selectScannedCode = useCallback(
        async (scannedCode: string) => {
            const info = await validateMarkingCode(scannedCode);
            if (!info) return;
            appendValidatedLine(info);
            setScanShow(false);
        },
        [appendValidatedLine, validateMarkingCode],
    );

    const pasteCode = useCallback(
        async (event: React.ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            const pastedCode = event.clipboardData
                .getData('text')
                .trim()
                .replace(/\s/g, '');
            changeCode(pastedCode);
            const info = await validateMarkingCode(pastedCode);
            if (info) setCodeInfo(info);
        },
        [changeCode, validateMarkingCode],
    );

    const updateLine = useCallback(
        (markingCode: string, field: 'lineAmount' | 'vat', value: string) => {
            const numericValue = value === '' ? '' : Number(value);
            onChange(
                updateTransferDocumentLine(lines, markingCode, (line) => {
                    const nextLine = {...line, [field]: numericValue};
                    return {
                        ...nextLine,
                        lineTaxAmount:
                            nextLine.lineAmount === '' || nextLine.vat === ''
                                ? ''
                                : roundMoney(
                                      (Number(nextLine.lineAmount) * Number(nextLine.vat)) / 100,
                                  ),
                    };
                }),
            );
        },
        [lines, onChange],
    );

    const columns = useMemo<Column<TableLineRow>[]>(
        () => [
            {
                accessor: 'lineNumber',
                Header: '#',
                width: 48,
                Cell: LineNumberCell,
            },
            {
                accessor: 'markingCode',
                width: 360,
                Header: (
                    <TableHeader>
                        <HeaderGroupItem>
                            {t('pages.documents.transfer.fields.markingCode')}
                            <RequiredTag>*</RequiredTag>
                        </HeaderGroupItem>
                        <small>GTIN (auto)</small>
                    </TableHeader>
                ),
                Cell: MarkingCodeCell,
            },
            {
                accessor: 'lineAmount',
                Header: `${t('pages.documents.transfer.fields.amount')} (${currency})`,
                width: 210,
                Cell: LineAmountCell,
            },
            {
                accessor: 'vat',
                Header: t('pages.documents.transfer.fields.vat'),
                width: 150,
                Cell: VatCell,
            },
            {
                accessor: 'lineTaxAmount',
                Header: t('pages.documents.transfer.fields.tax'),
                width: 210,
                Cell: TaxAmountCell,
            },
            {
                id: 'lineAmountWithVat',
                Header: `${t('pages.documents.transfer.fields.amountWithVat')} (${currency})`,
                width: 220,
                Cell: AmountWithVatCell,
            },
            {
                id: 'actions',
                Header: '',
                width: 52,
                Cell: ActionsCell,
            },
        ],
        [currency, t],
    );
    const modalColumns = useMemo<Column<TableLineRow>[]>(
        () =>
            columns.map((column, index) => ({
                ...column,
                width: [48, 300, 160, 110, 150, 180, 44][index],
            })),
        [columns],
    );

    const draftData = useMemo<TableLineRow[]>(
        () => [
            {
                isDraft: true,
                editable,
                showErrors,
                lineNumber: 0,
                lineAmount: 0,
                vat: 0,
                lineTaxAmount: 0,
                item: '',
                productName: '',
                productCode: '',
                markingCode: '',
                packagingType: codeInfo?.packagingType,
                draftCode: code,
                draftAmount: amount,
                draftVat: vat,
                codeIsValid: Boolean(codeInfo),
                markingCodePlaceholder: t('pages.documents.transfer.fields.markingCode'),
                amountPlaceholder: t('pages.documents.transfer.fields.amount'),
                vatPlaceholder: t('pages.documents.transfer.fields.vat'),
                onCodeChange: changeCode,
                onCodeBlur: checkCode,
                onCodePaste: pasteCode,
                onAmountChange: setAmount,
                onVatChange: setVat,
            },
        ],
        [amount, changeCode, checkCode, code, codeInfo, editable, pasteCode, showErrors, t, vat],
    );

    const toTableLine = useCallback(
        (line: TransferDocumentLine, depth = 0, canRemove = false): TableLineRow => {
            const mapLine = (
                item: TransferDocumentLine,
                itemDepth: number,
                itemCanRemove: boolean,
            ): TableLineRow => ({
                ...item,
                editable,
                showErrors,
                depth: itemDepth,
                canCopy:
                    !isTransportLine(item) &&
                    Number(item.lineAmount) > 0 &&
                    item.vat !== '' &&
                    !Number.isNaN(Number(item.vat)),
                canRemove: itemCanRemove,
                subRows: item.subRows?.map((child) => mapLine(child, itemDepth + 1, false)),
                onUpdate: (field, value) => updateLine(item.markingCode, field, value),
                onCopy: () => onChange(copyTransferDocumentLineData(lines, item.markingCode)),
                onRemove: () =>
                    onChange(
                        lines
                            .filter((storedLine) => storedLine.markingCode !== item.markingCode)
                            .map((storedLine, index) => ({
                                ...storedLine,
                                lineNumber: index + 1,
                            })),
                    ),
            });

            return mapLine(line, depth, canRemove);
        },
        [editable, lines, onChange, showErrors, updateLine],
    );

    const linesData = useMemo<TableLineRow[]>(
        () => lines.map((line) => toTableLine(line, 0, true)),
        [lines, toTableLine],
    );

    const selectedTransportLine = useMemo(
        () =>
            collectTransferDocumentLinesWithData(lines).find(
                (line) => line.markingCode === selectedTransportCode,
            ),
        [lines, selectedTransportCode],
    );
    const transportCodesData = useMemo(
        () => selectedTransportLine?.subRows?.map((line) => toTableLine(line, 0)) ?? [],
        [selectedTransportLine, toTableLine],
    );

    return (
        <Container>
            <Headline>
                <h3>
                    {t('pages.documents.transfer.sections.lines')}{' '}
                    <Highlighted>
                        ({countTransferDocumentCodes(lines)}{' '}
                        {t('pages.documents.transfer.itemsAdded')})
                    </Highlighted>
                </h3>
                <SectionDescription>
                    {editable && <span>{t('pages.documents.transfer.markedGoodsOnly')}</span>}
                </SectionDescription>
                <ButtonGroup>
                    {editable && (
                        <Button onClick={() => setScanShow(true)}>
                            {t('components.scan.button')}
                            <Icon source={ScanIcon} width={20} height={20} />
                        </Button>
                    )}
                </ButtonGroup>
            </Headline>
            <AddInvoiceLineForm
                onSubmit={(event) => {
                    event.preventDefault();
                    addLine();
                }}
            >
                {editable && (
                    <>
                        <LinesTable>
                            <Table
                                hoverableRow={false}
                                hasFooter={false}
                                hideSetting
                                showExpandableColumn={false}
                                columns={columns}
                                data={draftData}
                                tableViewExtraSettings={{overflowX: 'visible'}}
                                cellContainerSettings="width: 100%; min-height: 60px; align-items: center;"
                            />
                        </LinesTable>
                        <InvoiceLineTypeBanner
                            type={codeInfo ? (codeIsTransport ? 'transport' : 'unit') : null}
                        />
                        <Button
                            style={{width: 'fit-content', position: 'sticky', left: 0}}
                            size="sm"
                            variant={ButtonVariant.Secondary}
                            onMouseDown={(event) => event.preventDefault()}
                            disabled={
                                !code ||
                                (Boolean(codeInfo) &&
                                    !codeIsTransport &&
                                    (!amount || vat === '')) ||
                                pending
                            }
                            type="submit"
                        >
                            {t('pages.documents.transfer.addLine')}
                        </Button>
                    </>
                )}
                {!!linesData.length && (
                    <ExistingLinesTable hideHeader={editable}>
                        <Table
                            hoverableRow={false}
                            hasFooter={false}
                            hideSetting
                            showExpandableColumn
                            autoResetExpanded={false}
                            columns={columns}
                            data={linesData}
                            getRowCanExpand={(row) => {
                                const line = row.original as TransferDocumentLine;
                                return (
                                    line.packagingType === PackageTypes.BOX &&
                                    Boolean(line.subRows?.length)
                                );
                            }}
                            rowClassName={(row) => {
                                const line = row.original as TransferDocumentLine;
                                const hasModal =
                                    line.packagingType === PackageTypes.PALLET ||
                                    line.packagingType === PackageTypes.CONTAINER;
                                const isIncomplete =
                                    editable && !areTransferDocumentLinesValid([line]);

                                return [
                                    hasModal && 'transport-modal',
                                    isIncomplete && 'transport-incomplete',
                                ]
                                    .filter(Boolean)
                                    .join(' ');
                            }}
                            onRowClick={(row) => {
                                const line = (row.original ?? row) as TransferDocumentLine;
                                if (
                                    line.packagingType === PackageTypes.PALLET ||
                                    line.packagingType === PackageTypes.CONTAINER
                                ) {
                                    setSelectedTransportCode(line.markingCode);
                                }
                            }}
                            tableViewExtraSettings={{overflowX: 'visible'}}
                            cellContainerSettings="width: 100%; min-height: 60px; align-items: center;"
                        />
                    </ExistingLinesTable>
                )}
            </AddInvoiceLineForm>
            <Modal
                show={Boolean(selectedTransportLine)}
                onClose={() => setSelectedTransportCode(null)}
                title={
                    selectedTransportLine
                        ? `${t(
                              `dictionaries.products.packageType.${selectedTransportLine.packagingType}`,
                          )} ${selectedTransportLine.markingCode}`
                        : ''
                }
                modalWidth={3}
            >
                <TransportCodesModalContent>
                    <Table
                        hoverableRow={false}
                        hasFooter={false}
                        hideSetting
                        showExpandableColumn
                        autoResetExpanded={false}
                        columns={modalColumns}
                        data={transportCodesData}
                        getRowCanExpand={(row) => {
                            const line = row.original as TransferDocumentLine;
                            return isTransportLine(line) && Boolean(line.subRows?.length);
                        }}
                        rowClassName={(row) => {
                            const line = row.original as TransferDocumentLine;
                            return editable && !areTransferDocumentLinesValid([line])
                                ? 'transport-incomplete'
                                : undefined;
                        }}
                        tableViewExtraSettings={{overflowX: 'auto'}}
                        cellContainerSettings="width: 100%; min-height: 60px; align-items: center;"
                    />
                </TransportCodesModalContent>
            </Modal>
            <QRScannerModal
                className="qr-scanner-modal-invoices"
                open={scanShow}
                pending={pending}
                onClose={() => setScanShow(false)}
                onScan={selectScannedCode}
                onManual={selectScannedCode}
            />
        </Container>
    );
};
